import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "./../../assets/images/logo.png";
import SidebarUserProfile from "./SidebarUserProfile";
import { isAdminRole } from "../../utils/auth";
import {
  CampaignOutlined,
  CategoryOutlined,
  ChevronRight,
  ExpandMore,
  GroupOutlined,
  HomeOutlined,
  InsightsOutlined,
  ManageAccountsOutlined,
  PolicyOutlined,
  QueuePlayNextRounded,
  SubdirectoryArrowRightRounded,
  FeedbackOutlined,
  AccountBalanceOutlined,
  DashboardOutlined,
  MenuBookOutlined,
  SupportAgentOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";
import "./index.scss";

// ── Static nav tree (no component state/props dependencies) ──────────────────
// Kept outside the component so hooks that reference it are never blocked by
// "use before define" issues. Admin-only items are injected at render time.
function buildNavItems(includeAdminUsers) {
  const usersChildren = [
    {
      id: "users",
      label: "App Users",
      path: "/users",
      icon: <GroupOutlined fontSize="small" />,
    },
    {
      id: "users-statistics",
      label: "User Statistics",
      path: "/users-statistics",
      icon: <InsightsOutlined fontSize="small" />,
    },
  ];

  if (includeAdminUsers) {
    usersChildren.push({
      id: "admin-users",
      label: "Admin Users",
      path: "/admin-users",
      icon: <ManageAccountsOutlined fontSize="small" />,
    });
  }

  return [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/dashboard",
      icon: <HomeOutlined fontSize="small" />,
    },

    // ── Users group ──────────────────────────────────────────
    {
      id: "users-group",
      label: "Users",
      icon: <GroupOutlined fontSize="small" />,
      children: usersChildren,
    },

    // ── Content / Videos group ────────────────────────────────
    {
      id: "videos-group",
      label: "Videos",
      icon: <VideoLibraryOutlined fontSize="small" />,
      children: [
        {
          id: "videoUpload",
          label: "Video Library",
          path: "/videoUpload",
          icon: <QueuePlayNextRounded fontSize="small" />,
        },
        {
          id: "dashboards",
          label: "Video Dashboard",
          path: "/dashboards/analysis",
          icon: <DashboardOutlined fontSize="small" />,
        },
      ],
    },

    // ── Categories group ──────────────────────────────────────
    {
      id: "category-group",
      label: "Categories",
      icon: <CategoryOutlined fontSize="small" />,
      children: [
        {
          id: "category",
          label: "All Categories",
          path: "/category",
          icon: <CategoryOutlined fontSize="small" />,
        },
        {
          id: "subCategory",
          label: "Sub Categories",
          path: "/subCategory",
          icon: <SubdirectoryArrowRightRounded fontSize="small" />,
        },
      ],
    },

    // ── Standalone items ──────────────────────────────────────
    {
      id: "banners",
      label: "Banners",
      path: "/banners",
      icon: <CampaignOutlined fontSize="small" />,
    },
    {
      id: "government-schemes",
      label: "Gov. Schemes",
      path: "/government-schemes",
      icon: <AccountBalanceOutlined fontSize="small" />,
    },
    {
      id: "farm-dairy",
      label: "Farm Diary",
      path: "/farm-dairy",
      icon: <MenuBookOutlined fontSize="small" />,
    },
    {
      id: "feedback",
      label: "Feedback",
      path: "/feedback",
      icon: <FeedbackOutlined fontSize="small" />,
    },
    {
      id: "support",
      label: "Support Tickets",
      path: "/support",
      icon: <SupportAgentOutlined fontSize="small" />,
    },
    {
      id: "privacypolicy",
      label: "Privacy Policy",
      path: "/privacypolicy",
      icon: <PolicyOutlined fontSize="small" />,
    },
  ];
}

export default function SidebarComponent() {
  const location = useLocation();
  const navItems = buildNavItems(isAdminRole());

  // Helper: is a path/id active against the current URL?
  const isPathActive = (path, id) => {
    // Keep overview /dashboard distinct from Videos → /dashboards/*
    if (id === "dashboard") return location.pathname === "/dashboard";
    if (id === "dashboards") return location.pathname.startsWith("/dashboards");
    if (id === "admin-users") return location.pathname.startsWith("/admin-users");
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + "/")
    );
  };

  // Derive which group contains the currently active route.
  // Recomputed whenever the pathname changes, so a page refresh or direct URL
  // navigation will automatically open the correct submenu.
  const activeGroupId = useMemo(() => {
    for (const item of navItems) {
      if (item.children) {
        const hasActive = item.children.some((child) =>
          isPathActive(child.path, child.id)
        );
        if (hasActive) return item.id;
      }
    }
    return null;
    // location.pathname is the only thing isPathActive depends on from outside
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // openMenus tracks explicitly user-toggled groups. When null/undefined for
  // a group, we fall back to activeGroupId to decide whether it is open.
  const [openMenus, setOpenMenus] = useState({});

  // Accordion: clicking a parent closes every other open group.
  const toggleMenu = (id) => {
    setOpenMenus((prev) => {
      const isCurrentlyOpen = !!(prev[id] ?? (activeGroupId === id));
      // If it was open → close everything; if closed → open only this one.
      return isCurrentlyOpen ? { [id]: false } : { [id]: true };
    });
  };

  return (
    <aside className="sidebar-shell flex flex-col overflow-hidden">
      <div className="sidebar-brand flex-shrink-0">
        <img className="logo" src={logo} alt="KisanGrow Logo" />
        <div className="sidebar-brand__meta">
          <span className="sidebar-brand__eyebrow">Admin Manager</span>
          <span className="sidebar-brand__title">KisanGrow</span>
        </div>
      </div>
      <div className="sidebar-section-label flex-shrink-0">Navigation</div>
      <nav className="sidebar-nav listing min-h-0 flex-1 overflow-y-auto">
        {navItems.map((item) => {
          // ── Parent item with children (submenu) ──
          if (item.children) {
            // A group is open when explicitly toggled OR when it contains the active route.
            const isOpen = !!(openMenus[item.id] ?? (activeGroupId === item.id));
            const isAnyChildActive = item.children.some((child) =>
              isPathActive(child.path, child.id)
            );

            return (
              <div key={item.id} className="sidebar-menu-group">
                <button
                  type="button"
                  onClick={() => toggleMenu(item.id)}
                  className={`sidebar-link sidebar-link--parent sidebar-link--top ${
                    isAnyChildActive && !isOpen ? "sidebar-link--active" : ""
                  }`}
                >
                  <span className="sidebar-link__icon">{item.icon}</span>
                  <span className="sidebar-link__meta">
                    <span className="sidebar-link__label">{item.label}</span>
                  </span>
                  <ExpandMore
                    sx={{
                      marginLeft: "auto",
                      marginRight: "10px",
                      fontSize: 18,
                      opacity: 0.6,
                      flexShrink: 0,
                      transition: "transform 0.25s ease",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                <div
                  className="sidebar-submenu"
                  style={{
                    maxHeight: isOpen
                      ? `${item.children.length * 54}px`
                      : "0px",
                  }}
                >
                  {item.children.map((child) => {
                    const isChildActive = isPathActive(child.path, child.id);
                    return (
                      <Link
                        key={child.id}
                        to={child.path}
                        className={`sidebar-link sidebar-link--child ${
                          isChildActive ? "sidebar-link--active" : ""
                        }`}
                      >
                        <span className="sidebar-link__icon sidebar-link__icon--child">
                          {child.icon}
                        </span>
                        <span className="sidebar-link__meta">
                          <span className="sidebar-link__label">
                            {child.label}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          }

          // ── Standalone top-level item (same padding/alignment as parents) ──
          const isActive = isPathActive(item.path, item.id);
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`sidebar-link sidebar-link--top ${
                isActive ? "sidebar-link--active" : ""
              }`}
            >
              <span className="sidebar-link__icon">{item.icon}</span>
              <span className="sidebar-link__meta">
                <span className="sidebar-link__label">{item.label}</span>
                {item.description && (
                  <span className="sidebar-link__description">
                    {item.description}
                  </span>
                )}
              </span>
              <ChevronRight
                sx={{
                  marginLeft: "auto",
                  marginRight: "10px",
                  fontSize: 18,
                  opacity: 0.6,
                  flexShrink: 0,
                }}
              />
            </Link>
          );
        })}
      </nav>
      <SidebarUserProfile />
    </aside>
  );
}
